import { useCallback, useContext, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import Text from "../../styleguide/Text";
import SvgIcon from "../../helpers/SvgIcon";
import { ActionMenuComponentContext } from "../actionMenu/ActionMenuComponentContext";

type Props = {
  children: React.ReactNode;
  show: boolean;
  handleClose?: () => void;
  title?: string;
  modalSize?: "xs" | "sm" | "md" | "lg" | "xl";
  headerComponent?: (() => JSX.Element) | JSX.Element;
  actionsComponent?: (() => JSX.Element) | JSX.Element;
  disableEscapeKeyDown?: boolean;
};

const modalsRoot = document.getElementById("root-modals") || document.body;

const ModalWrapper = ({
  children,
  show,
  handleClose,
  title,
  modalSize = "sm",
  headerComponent,
  actionsComponent,
  disableEscapeKeyDown = false,
}: Props): any => {
  const actionMenuContext = useContext(ActionMenuComponentContext);
  const dialogElementRef = useRef<HTMLDivElement | null>(null);
  const latestDialogState = useRef({ show, actionMenuContext });
  latestDialogState.current = { show, actionMenuContext };

  const restoreActionFocus = useCallback(() => {
    const context = latestDialogState.current.actionMenuContext;
    if (!context) return;
    const activeElement = document.activeElement;
    // Do not steal focus from another dialog or a control the user already selected.
    if (
      activeElement?.isConnected &&
      activeElement !== document.body &&
      !dialogElementRef.current?.contains(activeElement)
    ) {
      return;
    }
    context.restoreFocus();
  }, []);

  useEffect(() => {
    return () => {
      // Conditional rendering can unmount an open dialog before its exit transition runs.
      if (latestDialogState.current.show) restoreActionFocus();
    };
  }, [restoreActionFocus]);

  return createPortal(
    <Dialog
      ref={(element) => {
        if (element) dialogElementRef.current = element;
      }}
      open={show}
      disableEscapeKeyDown={disableEscapeKeyDown}
      onClose={handleClose}
      TransitionProps={{ onExited: restoreActionFocus }}
      maxWidth={modalSize}
      fullWidth
      scroll="body"
      sx={{ paddingTop: "70px" }}>
      <ActionMenuComponentContext.Provider value={null}>
        {/* Header */}
        {(title || handleClose) && (
          <DialogTitle
            component="div"
            className="flex items-center justify-between gap-3">
            {title && (
              <Text
                variant="h2"
                component="h2"
                className="font-semibold text-slate-900">
                {title}
              </Text>
            )}
            {handleClose && (
              <IconButton
                onClick={handleClose}
                aria-label="Inchide"
                className="text-gray-500 hover:text-gray-900">
                <SvgIcon type="CLOSE_ICON" fill="currentColor" />
              </IconButton>
            )}
          </DialogTitle>
        )}

        {!!headerComponent && (
          <DialogTitle component="div">
            {typeof headerComponent === "function"
              ? headerComponent()
              : headerComponent}
          </DialogTitle>
        )}

        <DialogContent
          className="w-full border-t border-slate-200"
          sx={{ "&&": { px: 4, py: 4 } }}>
          {children}
        </DialogContent>

        {!!actionsComponent && (
          <DialogActions>
            {typeof actionsComponent === "function"
              ? actionsComponent()
              : actionsComponent}
          </DialogActions>
        )}
      </ActionMenuComponentContext.Provider>
    </Dialog>,
    modalsRoot,
  );
};

export default ModalWrapper;
